import { use_dispatch } from '@/hooks/_base/use_dispatch';
import { use_slice_generation, use_slice_ui, use_slice_workspace } from '@/hooks/store/use_slices';
import { LOCAL__ui_generation_set_hovered_button_grid2d_dimension } from '@/state/ui/generation/LOCAL__ui_generation_set_hovered_button_grid2d_dimension';
import { UTILS__graph_data } from '@/utils/UTILS__graph_data';
import { ENUM__workspace_entity_feature } from '@shared/dto/+workspace/workspace_entity/_enums/ENUM__workspace_entity_feature';
import { FIELD__base } from '@shared/dto/generation/graph_invoke/_fields/_base/FIELD__base';
import { FIELD_PRIMITIVE__base_string } from '@shared/dto/generation/graph_invoke/_fields/_primitives/string/FIELD_PRIMITIVE__base_string';
import { forwardRef, useState } from 'react';
import { G_HOOK__graph_field_hashed_for_hover } from '../_base/_hooks/G_HOOK__graph_field_hashed_for_hover';
import { G_BUTTON__field_string_list_item_change_visibility } from './_buttons/G_BUTTON__field_string_list_item_change_visibility';

interface PROPS {
  field: FIELD_PRIMITIVE__base_string;
}

export const G_UI_FIELD_PRIMITIVE__string_list_item = forwardRef<HTMLDivElement, PROPS>(({ field }, ref) => {
  /*
   * STORE
   */

  const dispatch = use_dispatch();

  const { g_general__ui_disabled, g_hovered_grid2d_column, g_hovered_grid2d_row } = use_slice_ui([
    'g_general__ui_disabled',
    'g_hovered_grid2d_column',
    'g_hovered_grid2d_row',
  ]);
  const { current_workspace_user } = use_slice_workspace(['current_workspace_user']);
  const { generation_current } = use_slice_generation(['generation_current']);
  /*
   * STATE
   */

  let { graph } = generation_current.tool;

  const [is_mouse_over, set_is_mouse_over] = useState(false);

  const field_hashed = G_HOOK__graph_field_hashed_for_hover(field);

  /*
   * EVENTS
   */

  async function onClick() {
    if (is_disabled()) return;

    let field_from_graph = graph.field__get_primitive(field.meta);

    if (field.get_value() == field_from_graph.get_value()) return;

    let values_allowed = field.get_values_allowed();

    let values_range = field.get_value_range();
    values_range.push(field.get_value());
    values_range = values_range.unique_values();

    values_range.sort(item => values_allowed.indexOf(item));

    field.set_value_range(values_range);

    await UTILS__graph_data.update_field(graph, field);
  }

  function onMouseEnter() {
    set_is_mouse_over(true);

    if (field.meta.grid2d__is_column) {
      dispatch(
        LOCAL__ui_generation_set_hovered_button_grid2d_dimension({ g_hovered_grid2d_column: field_hashed, g_hovered_grid2d_dimension_from_grid: false }),
      );
    } else if (field.meta.grid2d__is_row) {
      dispatch(LOCAL__ui_generation_set_hovered_button_grid2d_dimension({ g_hovered_grid2d_row: field_hashed, g_hovered_grid2d_dimension_from_grid: false }));
    }
  }

  function onMouseLeave() {
    set_is_mouse_over(false);
    dispatch(LOCAL__ui_generation_set_hovered_button_grid2d_dimension());
  }

  /*
   * METHODS
   */

  function get_class_name_grid2d_dimension(grid2d_dimension: FIELD__base) {
    if (!(grid2d_dimension instanceof FIELD_PRIMITIVE__base_string)) return '';
    if (grid2d_dimension.meta.field_id != field.meta.field_id) return '';
    if (grid2d_dimension.get_value() != field.get_value()) return '';

    return 'g_sidebar_list_item_hovered !border-solid';
  }

  function is_used_in_generation() {
    let field_from_graph = generation_current.tool.graph.field__get_primitive(field.meta);

    return field_from_graph.get_value() == field.get_value();
  }

  function is_disabled() {
    return (
      g_general__ui_disabled ||
      !current_workspace_user.is_feature_pertmitted(ENUM__workspace_entity_feature.ENTITY_GENERATION_UPDATE) ||
      field.meta.ui__is_readonly
    );
  }

  /*
   * HTML
   */

  return (
    <div
      ref={ref}
      key={`${field.meta.field_id}_${field.get_value()}`}
      {...{ onClick, onMouseEnter, onMouseLeave }}
      className={`
            g_sidebar_list_item gap-2 px-2 w-full
            ${is_used_in_generation() && 'g_sidebar_list_item_hovered border-dashed font-bold'}
            ${!field.get_value_range().includes(field.get_value()) && 'opacity-50'}

            ${get_class_name_grid2d_dimension(g_hovered_grid2d_column)}
            ${get_class_name_grid2d_dimension(g_hovered_grid2d_row)}
          `}
    >
      <div className="truncate flex-1">{field.get_value()}</div>
      {is_mouse_over && <G_BUTTON__field_string_list_item_change_visibility {...{ field }} />}
    </div>
  );
});
